import { useEffect, useState } from "react";
import Button from '@components/legacy/core/Button';
import { useTranslations } from "../../../context/TranslationContext";
import Icon from "../../../core/Icon";
import ModalComponent from "../../../ModalComponent";
import { Card, CardFooter, CardMedia } from "../../../Cards";
import ImageUpload from "../../../legacy/core/ImageUpload";
import fetchCustom from "../../../../wp_helpers/fetchCustom";
import getResponseJson from "../../../../wp_helpers/getResponseJson";

// setUrl (from the settings metadata) only powers the empty "Add" state; once a
// background exists, the GET response carries its own set_url/delete_url and the
// Edit/Remove buttons follow those.
const VideoBackground = ({ getUrl, setUrl }) => {
    const { t } = useTranslations();
    const [background, setBackground] = useState(null);
    const [fetched, setFetched] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const getBackground = async () => {
        if (!getUrl) return;
        const response = await fetchCustom(getUrl);
        const responseJson = await getResponseJson(response, { notification: false });
        if (response.ok) {
            setBackground(responseJson?.background || null);
        } else if (response.status === 404) {
            // No background configured is a 404, not a 200 with null.
            setBackground(null);
        }
        setFetched(true);
    };

    useEffect(() => {
        getBackground();
    }, [getUrl]);

    const handleOpenModal = () => {
        setSelectedFile(null);
        setError('');
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
    };

    const effectiveSetUrl = background?.set_url || setUrl;

    const handleSetImage = async () => {
        if (!selectedFile || !effectiveSetUrl) return;
        setSaving(true);
        setError('');
        const formData = new FormData();
        formData.append('image', selectedFile);
        const response = await fetchCustom(effectiveSetUrl, {
            method: 'POST',
            body: formData,
        });
        const responseJson = await getResponseJson(response);
        setSaving(false);
        if (response.ok) {
            setBackground(responseJson?.background || null);
            handleCloseModal();
        } else {
            setError(responseJson?.message || t('Something went wrong, please try again'));
        }
    };

    const handleRemove = () => {
        const deleteUrl = background?.delete_url;
        if (!deleteUrl) return;
        window.show_confirmation_overlay(
            'delete-video-background',
            t('Confirmation'),
            t('Are you sure you want to remove the video background image?'),
            {},
            {
                label: t('Remove'),
                action: async () => {
                    const response = await fetchCustom(deleteUrl, { method: 'DELETE' });
                    await getResponseJson(response);
                    if (response.ok) {
                        setBackground(null);
                    }
                },
            },
        );
    };

    if (!fetched || (!getUrl && !setUrl)) return null;

    return (
        <div className="VideoBackground margin-vertical-xl">
            <h5>{t('Video background')}</h5>
            {background ? (
                <Card className="VideoBackground-card">
                    <CardMedia>
                        <img src={background.url} alt={t('Video background')} />
                    </CardMedia>
                    <CardFooter>
                        {background.delete_url && (
                            <div className="btn-container">
                                <Button type="link-secondary" onClick={handleRemove}>{t('Remove')}</Button>
                            </div>
                        )}
                        {background.set_url && (
                            <div className="btn-container">
                                <Button type="secondary" onClick={handleOpenModal}>{t('Edit video background')}</Button>
                            </div>
                        )}
                    </CardFooter>
                </Card>
            ) : (
                setUrl && (
                    <div className="btn-container">
                        <Button type="secondary" onClick={handleOpenModal}>
                            <Icon value="plus" />
                            {t('Add video background')}
                        </Button>
                    </div>
                )
            )}
            <ModalComponent show={showModal} closeModal={handleCloseModal}>
                <div className="overlay">
                    <div className="overlay-header">
                        <span
                            className="overlay-cancel overlay-cancel-mark"
                            role="button"
                            tabIndex={0}
                            aria-label={t('Close')}
                            onClick={handleCloseModal}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    handleCloseModal();
                                }
                            }}
                        />
                        <h3>{background ? t('Edit video background') : t('Add video background')}</h3>
                    </div>
                    <div className="overlay-main">
                        <p>{t('This image will be shown as the background when video questions are recorded.')}</p>
                        <p className="VideoBackground-guidance">
                            {t('For best results, use a landscape image at least 1280×720px (16:9). Anything else is cropped to fit the recorder frame, so keep the important part of the picture centered.')}
                        </p>
                        <ImageUpload
                            name="video-background-file-input"
                            accept="image/png,image/jpeg,image/webp"
                            uploadButtonText={t('Choose image')}
                            value={null}
                            onChange={setSelectedFile}
                        />
                        {error && <div className="VideoBackground-error">{error}</div>}
                    </div>
                    <div className="overlay-footer">
                        <div className="btn-container">
                            <Button type="primary" onClick={handleSetImage} disabled={!selectedFile || saving}>
                                {saving ? t('Uploading...') : t('Set image')}
                            </Button>
                        </div>
                    </div>
                </div>
            </ModalComponent>
        </div>
    );
};

export default VideoBackground;
